import React, {useCallback} from 'react';
import {IconButton, styled} from '@mui/material';
import {designSystemToken, Icon} from '@lightricks/react-design-system';
import {getApprovalBreakdown} from '@/utils/approvalStatusUtils';
import {ContentSubmission} from '@/types/contentSubmission';
import {
  ContentSubmissionAction,
  useContentSubmissionActionMenuItems,
} from '@/components/content-submission-card';
import {CONTENT_SUBMISSION_ACTION} from '@/components/content-submission-card/contentSubmissionCardTypes';
import Dropdown, {DropdownOption} from '@/components/dropdown/Dropdown';
import useDownload from '@/hooks/use-download/useDownload';
import {
  buildDownloadFileName,
  resolveDownloadUrl,
} from '@/hooks/use-download/utils';
import useContentSubmissionOffPlatformIdentityStore from '@/stores/contentSubmissionOffPlatformIdentityStore';

const EMPTY_SELECTED_IDS: string[] = [];

type ContentSubmissionRowMenuProps = {
  contentSubmission: ContentSubmission;
  campaignId: string;
  campaignState?: string | null;
  isAdmin?: boolean;
  isOffPlatform?: boolean;
  onAction: (
    action: ContentSubmissionAction,
    contentSubmissionId: string,
    contentSubmissionVersionId?: string
  ) => void;
  onMenuOpen?: (context: {creator_id?: string; flow_id: string}) => void;
  onActionSelected?: (
    action: ContentSubmissionAction,
    context: {creator_id?: string; flow_id: string}
  ) => void;
};

const StyledIconButton = styled(IconButton)({
  height: '30px',
  width: '30px',
});

const StyledIcon = styled(Icon)({
  transform: 'scale(1.5)',
});

function ContentSubmissionRowMenu({
  contentSubmission,
  campaignId,
  campaignState,
  isAdmin = false,
  isOffPlatform = false,
  onAction,
  onMenuOpen,
  onActionSelected,
}: ContentSubmissionRowMenuProps) {
  const identity = useContentSubmissionOffPlatformIdentityStore((s) =>
    campaignId ? (s.identities[campaignId] ?? null) : null
  );

  const downloadUrl = resolveDownloadUrl(contentSubmission);
  const downloadFileName = downloadUrl
    ? buildDownloadFileName(contentSubmission, downloadUrl)
    : undefined;
  const {
    status: downloadStatus,
    progress: downloadProgress,
    download,
  } = useDownload({
    id: contentSubmission.id,
    url: downloadUrl,
    fileName: downloadFileName,
  });

  const roleAlreadyApproved = (() => {
    if (!isOffPlatform || !identity?.role) return false;
    const {approvedStepsNames} = getApprovalBreakdown(
      contentSubmission.approvalStatus
    );
    return approvedStepsNames.includes(identity.role);
  })();

  const {items, onMenuClose} = useContentSubmissionActionMenuItems({
    isAdmin,
    analysisStatus: contentSubmission.latestVersionAnalysisStatus,
    isOffPlatform,
    kind: contentSubmission.kind,
    aasmState: contentSubmission.aasmState,
    archivedFromState: contentSubmission.archivedFromState,
    campaignState,
    roleAlreadyApproved,
    parentContentSubmissionId: contentSubmission.parentContentSubmissionId,
    downloadStatus: {status: downloadStatus, progress: downloadProgress},
    hasDownloadUrl: !!downloadUrl,
  });

  const isTransitioned = contentSubmission.previousAasmState != null;
  const visibleItems = isTransitioned ? [] : items;

  const handleOpen = useCallback(() => {
    onMenuOpen?.({
      creator_id: contentSubmission.profile?.id,
      flow_id: contentSubmission.id,
    });
  }, [onMenuOpen, contentSubmission]);

  const handleChange = useCallback(
    async (option: DropdownOption) => {
      const item = visibleItems.find((i) => i.id === option.id);
      if (!item) {
        return;
      }
      if (item.action === CONTENT_SUBMISSION_ACTION.DOWNLOAD) {
        download();
        return;
      }
      onActionSelected?.(item.action, {
        creator_id: contentSubmission.profile?.id,
        flow_id: contentSubmission.id,
      });
      onAction(
        item.action,
        contentSubmission.id,
        contentSubmission.latestVersionId ?? undefined
      );
      await onMenuClose(contentSubmission);
    },
    [
      visibleItems,
      onAction,
      onActionSelected,
      onMenuClose,
      contentSubmission,
      download,
    ]
  );

  return (
    <Dropdown
      label=""
      options={visibleItems}
      selectedIds={EMPTY_SELECTED_IDS}
      onChange={handleChange}
      selectionMode="single"
      menuWidth={200}
      showItemDividers={false}
      menuPositionHorizontal="left"
      renderTrigger={({onClick}) => (
        <StyledIconButton
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
            onClick(e);
          }}
          size="small"
        >
          <StyledIcon
            size="small"
            appearance="neutral"
            name="Actions-More-Vertical"
            color={designSystemToken('semantic.fg.tertiary')}
          />
        </StyledIconButton>
      )}
    />
  );
}

export default ContentSubmissionRowMenu;
